import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { useAttachmentUrl } from 'hooks/useAttachmentUrl';
import { useClickAttachment } from 'hooks/useClickAttachment';
import { SummaryAttachment } from 'models/aiArtifacts/Summary';
import colors from 'styles/theme/colors';

import { formatProvenance } from './provenanceText';

export type CitationBadgeVariant = 'purple' | 'white';

type CitationBadgeProps = {
  attachment: SummaryAttachment;
  size?: number;
  // using a negative because most components will want to show the hover effect
  hideHoverEffect?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  attachmentType?: string;
  /**
   * Page within the cited PDF to render in the hover preview. When undefined,
   * the badge falls back to a simple filename tooltip (no preview).
   */
  page?: number;
};

export default function CitationBadge({
  attachment,
  size = 16,
  hideHoverEffect = false,
  clickable = true,
  onClick,
  attachmentType,
  page,
}: CitationBadgeProps) {
  const openAttachment = useClickAttachment(attachment.attachmentId, attachmentType, page);
  const handleClick = onClick ?? openAttachment;

  return (
    <StyledCitationBadge
      size={size}
      hoverable={!hideHoverEffect}
      clickable={clickable || !!onClick}
      variant='purple'
      onClick={clickable || !!onClick ? handleClick : undefined}>
      {attachment.displayId}
      {!hideHoverEffect && <PreviewLayer source={attachment} attachmentType={attachmentType} page={page} />}
    </StyledCitationBadge>
  );
}

type PreviewProps = {
  source: SummaryAttachment;
  attachmentType?: string;
  page?: number;
};

function PreviewLayer({ source, attachmentType, page }: PreviewProps) {
  const isPdf = /\.pdf$/i.test(source.filename ?? '');
  const showPdfFrame = isPdf && typeof page === 'number';
  const { url, fetch } = useAttachmentUrl(source.attachmentId, attachmentType);
  const provenanceLine = formatProvenance(source);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0, flipUp: false });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cardWidth = 380;
  const estimatedHeight = showPdfFrame ? 480 : 80;
  const gap = 10;

  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setVisible(false), 120);
  };

  const handleEnter = () => {
    cancelHide();
    if (showPdfFrame) fetch();
    if (!triggerRef.current) return;
    const parent = triggerRef.current.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < estimatedHeight + gap + 8 && rect.top > estimatedHeight + gap + 8;
    const y = flipUp ? rect.top - estimatedHeight - gap : rect.bottom + gap;
    let x = rect.left + rect.width / 2 - cardWidth / 2;
    x = Math.max(8, Math.min(x, window.innerWidth - cardWidth - 8));
    setCoords({ x, y, flipUp });
    setVisible(true);
  };

  useLayoutEffect(() => () => cancelHide(), []);

  return (
    <>
      <HoverArea ref={triggerRef} onMouseEnter={handleEnter} onMouseLeave={scheduleHide} />
      {visible &&
        createPortal(
          <PreviewCard
            style={{ top: coords.y, left: coords.x, width: cardWidth }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}>
            <HoverBridge $flipUp={coords.flipUp} $gap={gap} />
            <Caption>{showPdfFrame ? `Page ${page} of ${source.filename}` : source.filename}</Caption>
            {provenanceLine && <Provenance>{provenanceLine}</Provenance>}
            {showPdfFrame && (
              <PdfFrameWrapper>
                {url ? (
                  <iframe
                    title={`${source.filename} page ${page}`}
                    src={`${url}#page=${page}&toolbar=0&navpanes=0&view=FitH`}
                  />
                ) : (
                  <Loading>Loading preview…</Loading>
                )}
              </PdfFrameWrapper>
            )}
          </PreviewCard>,
          document.body
        )}
    </>
  );
}

const purpleStyles = css`
  background: ${colors.aiPurpleLight20};
  color: #3e2d6e;
  border-radius: 26px;
`;

const whiteStyles = css`
  background: ${colors.white};
  color: ${colors.black};
  border: 0.5px solid ${colors.black25};
  border-radius: 26px;

  &:hover {
    background: ${colors.primaryBlue05};
    border-color: ${colors.primaryBlue};
  }
`;

export const StyledCitationBadge = styled.span<{
  size: number;
  hoverable: boolean;
  clickable: boolean;
  variant: CitationBadgeVariant;
}>`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 8px;
  font-size: 8px;
  font-weight: 700;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  ${({ variant }) => (variant === 'white' ? whiteStyles : purpleStyles)}
  ${({ hoverable, variant }) =>
    hoverable &&
    variant === 'purple' &&
    css`
      &:hover {
        border: 0.5px solid #3e2d6e;
      }
    `}
`;

const HoverArea = styled.span`
  position: absolute;
  inset: 0;
`;

const PreviewCard = styled.div`
  position: fixed;
  z-index: 9999;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HoverBridge = styled.span<{ $flipUp: boolean; $gap: number }>`
  position: absolute;
  left: 0;
  right: 0;
  height: ${({ $gap }) => $gap}px;
  ${({ $flipUp }) => ($flipUp ? 'bottom: -' : 'top: -')}${({ $gap }) => $gap}px;
  background: transparent;
`;

const PdfFrameWrapper = styled.div`
  width: 100%;
  height: 460px;
  border-radius: 4px;
  overflow: hidden;
  background: ${colors.black05};

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    pointer-events: none;
  }
`;

const Loading = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${colors.black50};
`;

const Caption = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${colors.black};
`;

const Provenance = styled.div`
  font-style: italic;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: ${colors.black50};
  margin-top: -4px;
`;
